import { useTranslation } from "react-i18next"
import PageTitle from "../../components/PageTitle"
import styled, { useTheme } from "styled-components"
import { useEffect, useMemo, useState } from "react"
import Comm from "../../Comm"
import ChargerType from "../../types/ChargerType"
import LogType from "../../types/LogType"
import Icon from "../../Icon"
import ButtonWithIcon from "../../components/ButtonWithIcon"
import LoaderDots from "../../components/LoaderDots"
import InputWithIcon from "../../components/InputWithIcon"
import WhiteSpace from "../../components/Whitespace"
import normalizeString from "../../utils/normalizeString"
import Dropdown from "../../components/Dropdown"
import { getLogCodeType, getLogCodeLabel, getLogCodesByType } from "../../utils/logCodes"
import Modal from "../../components/Modal"
import DateRangePickerWithPresets from "../../components/DateRangePickerWithPresets"
import Table from "../../modules/Table"
import { TableCellTypes } from "../../enums/TableCellTypes"
import ChargerDetailsModal, { ChargerDetailsHint } from "../../modules/ChargerDetailsModal"
import { ChargerFilterModal, chargerMatchesFilter, countActiveChargerFilters, ChargerFilter } from "../../modules/ChargerFilter"
import { Line } from "react-chartjs-2"
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend } from "chart.js"
import { BREAKPOINTS, TAP_MIN } from "../../GlobalTheme"

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler, Legend)

const ToolbarContainer = styled.div`
  position: absolute;
  top: 0px;
  right: 12px;
  display: flex;
  /* Absolutely positioned toolbars cannot wrap, so on a phone the button row overlays the page
     title and its leading buttons land at negative x, which no amount of panning can reach.
     Put it back in flow and let it wrap. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    position: static;
    top: auto;
    right: auto;
    left: auto;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
  }
`

const ChartContainer = styled.div`
  position: absolute;
  top: 50px;
  right: 12px;
  width: 200px;
  height: 50px;
  //border: 1px solid red;
`

const RefreshTimerLabel = styled.p`
  font-weight: 400;
  font-size: 10px;
  color: ${props => props.theme.colors.gray_600};
  margin-top: 4px;
  text-align: center;
`

const ListContainer = styled.div`
  width: 100%;
  height: calc(90vh - 120px);
  @media (max-width: ${BREAKPOINTS.shell}px) {
    height: auto;
    max-height: calc(100dvh - 160px);
    overscroll-behavior: contain;
  }
  overflow-y: scroll;
  border-radius: 16px;
`

const ChargerRow = styled.div<{ $isOpen: boolean }>`
  background-color: ${props => props.theme.colors.white};
  border-bottom: 1px dashed ${props => props.theme.colors.gray_300};
  cursor: pointer;

  /* The row scrolls horizontally on a phone. Without this the white background and the dashed rule
     stop at the viewport edge, so panning right leaves the content on bare page background. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    width: max-content;
    min-width: 100%;
  }
`

const ChargerRowInner = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 16px;
`

const ChargerId = styled.p`
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_900};
  margin: 0px;
`

const ChargerEmail = styled.p`
  font-weight: 400;
  font-size: 14px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  margin-top: 2px;
`

const LogCount = styled.span<{ $themeColor: string, $bgColor: string }>`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors[props.$themeColor]};
  background-color: ${props => props.theme.colors[props.$bgColor]};
  border-radius: 8px;
  padding: 4px 14px;
  margin-left: 8px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  opacity: 0.9;
`

const LastLogTime = styled.p`
  font-weight: 400;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  margin: 0px;
  margin-left: auto;
  margin-right: 16px;
  white-space: nowrap;
`

const ArrowContainer = styled.div<{ $isOpen: boolean }>`
  margin-right: 8px;
  transition: transform 0.2s;
  transform: rotate(${props => props.$isOpen ? '90deg' : '0deg'});
`

const LogsDropdown = styled.div`
  background-color: ${props => props.theme.colors.gray_100};
  border-top: 1px dashed ${props => props.theme.colors.gray_300};
  padding: 0px 8px 16px 8px;
  margin-top: 4px;
`

const LogCode = styled.p<{ $code: string }>` // normal / warning / error
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.$code == 'normal' ? props.theme.colors.black : props.theme.colors.white};
  border-radius: 40px;
  //width: 70px;
  padding-left: 12px;
  padding-right: 12px;
  line-height: 22px;
  text-align: center;
  margin: 0px;
  margin-left: -8px;
  margin-right: 24px;
  background-color: ${props => {
    switch (props.$code) {
      case 'normal':
        return props.theme.colors.gray_200
      case 'warning':
        return props.theme.colors.warning
      case 'error':
        return props.theme.colors.danger
    }
  }};
  opacity: 0.9;
`

const LogIndicator = styled.div<{ $code: string }>` // normal / warning / error
  width: 3px;
  height: 28px;
  margin-top: 2px;
  margin-bottom: 2px;
  border-radius: 3px;
  background-color: ${props => {
    switch (props.$code) {
      case 'normal':
        return props.theme.colors.primary
      case 'warning':
        return props.theme.colors.warning
      case 'error':
        return props.theme.colors.danger
    }
  }};
`

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  /* Keeps the trailing controls clear of the 200px trend chart absolutely placed at the top right. */
  padding-right: 220px;

  /* Search, filter, four pills and a time picker need ~900px. With nowrap the later items pile onto
     the earlier ones instead of moving down. */
  @media (max-width: ${BREAKPOINTS.shell}px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`

const FilterButton = styled.div<{ $active: boolean, $themeColor: string }>`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.$active ? props.theme.colors.white : props.theme.colors.gray_700};
  background-color: ${props => props.$active ? props.theme.colors[props.$themeColor] : props.theme.colors.gray_200};
  border-radius: 40px;
  padding: 6px 16px;
  cursor: pointer;
  margin-right: 8px;
  transition: background-color 0.15s;
  &:hover {
    opacity: 0.85;
  }

  @media (max-width: ${BREAKPOINTS.shell}px) {
    min-height: ${TAP_MIN}px;
    display: inline-flex;
    align-items: center;
  }
`

type Filter = 'all' | 'error' | 'warning'
type CodeFilter = 'all' | 'evse'
type TimeFilter = '1h' | 1 | 2 | 7 | 14 | 28 | 'all' | 'custom'

interface ChargerWithLogs extends ChargerType {
  logs: LogType[]
}

export default function ErrorLogs() {
  const { t } = useTranslation()

  const [chargersWithLogs, setChargersWithLogs] = useState<ChargerWithLogs[]>()
  const [detailsCharger, setDetailsCharger] = useState<ChargerType | null>(null)
  const [chargerFilter, setChargerFilter] = useState<ChargerFilter>({})
  const [isShowingFilterModal, setIsShowingFilterModal] = useState<boolean>(false)
  const [lastRefresh, setLastRefresh] = useState<string>('')
  const [openChargers, setOpenChargers] = useState<{ [key: string]: boolean }>({})
  const [filter, setFilter] = useState<Filter>('all')
  const [timeFilter, setTimeFilter] = useState<TimeFilter>(2)
  const [customDateRange, setCustomDateRange] = useState<Date[]>()
  const [codeFilter, setCodeFilter] = useState<CodeFilter>('all')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [timeFilterLabel, setTimeFilterLabel] = useState<string>('')
  const [isChartModalOpen, setIsChartModalOpen] = useState<boolean>(false)

  async function fetchData() {
    const chargersResponse = await Comm('/api-charger/chargers', {})
    const chargers: ChargerType[] = await chargersResponse.json()

    const errorAndWarningCodes = getLogCodesByType('error', 'warning').join(',')
    const logsResponse = await Comm(`/api-log/v2/charger/log/frontend?logCode=${errorAndWarningCodes}&pageSize=1000000000`, {
      headers: { "Charger_id": "all" }
    })
    const logsResult = await logsResponse.json()
    const logs: LogType[] = logsResult.data

    // Group logs by chargerId
    const logsByCharger: { [key: string]: LogType[] } = {}
    for (const log of logs) {
      if (!logsByCharger[log.chargerId]) {
        logsByCharger[log.chargerId] = []
      }
      logsByCharger[log.chargerId].push(log)
    }

    // Merge chargers with their logs, only include chargers that have logs
    const result: ChargerWithLogs[] = []
    for (const charger of chargers) {
      if (logsByCharger[charger.chargerId]) {
        result.push({ ...charger, availableLogDays: [], logs: logsByCharger[charger.chargerId] })
      }
    }

    // Sort by newest log timestamp descending
    result.sort((a, b) => {
      const newestA = Math.max(...a.logs.map(log => log.timestamp))
      const newestB = Math.max(...b.logs.map(log => log.timestamp))
      return newestB - newestA
    })

    setChargersWithLogs(result)
    setLastRefresh(new Date().toString().split(' ')[4])
  }

  useEffect(() => {
    fetchData()
  }, [])

  function handelRefresh() {
    setChargersWithLogs(undefined)
    fetchData()
  }

  const [allOpen, setAllOpen] = useState<boolean>(false)

  function toggleAllChargers() {
    if (allOpen) {
      setOpenChargers({})
    } else {
      const all: { [key: string]: boolean } = {}
      chargersWithLogs?.filter(chargerHasMatchingLogs).forEach(c => { all[c.chargerId] = true })
      setOpenChargers(all)
    }
    setAllOpen(!allOpen)
  }

  function toggleCharger(chargerId: string) {
    setOpenChargers(prev => ({ ...prev, [chargerId]: !prev[chargerId] }))
  }

  function applyTimeFilter(logs: LogType[]): LogType[] {
    if (timeFilter == 'all') return logs
    if (timeFilter == 'custom' && customDateRange) {
      const startStr = customDateRange[0].toISOString().split('T')[0]
      const endStr = customDateRange.length > 1 ? customDateRange[1].toISOString().split('T')[0] : startStr
      return logs.filter(log => log.day >= startStr && log.day <= endStr)
    }
    if (timeFilter == 'custom') return logs
    if (timeFilter == '1h') {
      const cutoff = Math.floor(Date.now() / 1000) - 3600
      return logs.filter(log => log.timestamp >= cutoff)
    }
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - timeFilter)
    const cutoffStr = cutoff.toISOString().split('T')[0]
    return logs.filter(log => log.day >= cutoffStr)
  }

  function applyCodeFilter(logs: LogType[]): LogType[] {
    if (codeFilter == 'all') return logs
    if (codeFilter == 'evse') return logs.filter(log => getLogCodeLabel(log.logCode).toLowerCase().includes('evse'))
    return logs
  }

  function getFilteredLogs(logs: LogType[]): LogType[] {
    let filtered = applyTimeFilter(logs)
    if (filter != 'all') {
      filtered = filtered.filter(log => getLogCodeType(log.logCode) == filter)
    }
    filtered = applyCodeFilter(filtered)
    if (searchTerm && searchTerm.length >= 3) {
      const normalized = normalizeString(searchTerm)
      filtered = filtered.filter(log => normalizeString(`${log.chargerId} ${log.day} ${log.time} ${log.lineNumber} ${log.logCode} ${getLogCodeLabel(log.logCode)} ${log.appTs} ${log.message}`).includes(normalized))
    }
    return filtered
  }

  function getLogCounts(logs: LogType[]) {
    let errors = 0
    let warnings = 0
    for (const log of logs) {
      if (getLogCodeType(log.logCode) == 'error') errors++
      else warnings++
    }
    return { errors, warnings }
  }

  function chargerMatchesSearch(charger: ChargerWithLogs): boolean {
    if (!searchTerm || searchTerm.length < 3) return false
    const normalized = normalizeString(searchTerm)
    return normalizeString(charger.userEmail).includes(normalized) || normalizeString(charger.localName).includes(normalized)
  }

  function chargerHasMatchingLogs(charger: ChargerWithLogs): boolean {
    if (chargerMatchesSearch(charger)) return applyCodeFilter(applyTimeFilter(charger.logs).filter(log => filter == 'all' || getLogCodeType(log.logCode) == filter)).length > 0
    return getFilteredLogs(charger.logs).length > 0
  }

  const styledTheme = useTheme()

  const chartData = useMemo(() => {
    if (!chargersWithLogs) return null
    const allLogs = chargersWithLogs.flatMap(c => c.logs)
    const timeFiltered = applyTimeFilter(allLogs)

    const dailyCounts: { [day: string]: { errors: number, warnings: number } } = {}
    for (const log of timeFiltered) {
      if (!dailyCounts[log.day]) dailyCounts[log.day] = { errors: 0, warnings: 0 }
      if (getLogCodeType(log.logCode) == 'error') dailyCounts[log.day].errors++
      else dailyCounts[log.day].warnings++
    }

    const days = Object.keys(dailyCounts).sort()
    return {
      labels: days.map(d => d.slice(5).replace('-', '.')),
      datasets: [
        {
          label: t('logs_errors'),
          data: days.map(d => dailyCounts[d].errors),
          borderColor: styledTheme.colors.danger,
          backgroundColor: styledTheme.colors.danger + '18',
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.4,
          fill: true,
        },
        {
          label: t('logs_warnings'),
          data: days.map(d => dailyCounts[d].warnings),
          borderColor: styledTheme.colors.warningActive,
          backgroundColor: styledTheme.colors.warningActive + '18',
          borderWidth: 1.5,
          pointRadius: 0,
          tension: 0.4,
          fill: true,
        },
      ],
    }
    // The series colours come from the theme and the labels from the translator, so both have to
    // invalidate this memo - without them a theme or language switch left the chart painted in the
    // previous theme's colours until the data happened to change.
    // applyTimeFilter is deliberately NOT a dependency: it is redeclared every render, so including
    // it would recompute on every render and defeat the memo. Its inputs are already listed.
  }, [chargersWithLogs, timeFilter, customDateRange, t, styledTheme])

  const smallChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    events: [] as ('mousemove' | 'mouseout' | 'click' | 'touchstart' | 'touchmove')[],
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    scales: {
      x: { display: false },
      y: { display: false, beginAtZero: true },
    },
  }

  const modalChartData = useMemo(() => {
    if (!chartData) return null
    return {
      ...chartData,
      datasets: chartData.datasets.map(ds => ({
        ...ds,
        borderWidth: 2,
        pointRadius: 3,
        pointHoverRadius: 6,
        pointBackgroundColor: ds.borderColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        backgroundColor: ds.borderColor + '15',
      })),
    }
  }, [chartData])

  const modalChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          font: { size: 12, weight: '600' as const },
          color: styledTheme.colors.gray_700,
          padding: 20,
        },
      },
      tooltip: {
        enabled: true,
        mode: 'index' as const,
        intersect: false,
        backgroundColor: '#fff',
        titleColor: styledTheme.colors.gray_900,
        bodyColor: styledTheme.colors.gray_700,
        titleFont: { size: 12, weight: '700' as const },
        bodyFont: { size: 12 },
        padding: 12,
        cornerRadius: 10,
        borderColor: styledTheme.colors.gray_300,
        borderWidth: 1,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        usePointStyle: true,
      },
    },
    scales: {
      x: {
        display: true,
        border: { display: false },
        grid: { display: false },
        ticks: {
          color: styledTheme.colors.gray_500,
          font: { size: 11 },
          maxRotation: 0,
          autoSkipPadding: 12,
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        border: { display: false },
        grid: {
          color: styledTheme.colors.gray_200,
          drawTicks: false,
        },
        ticks: {
          color: styledTheme.colors.gray_500,
          font: { size: 11 },
          precision: 0,
          padding: 8,
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    layout: {
      padding: { top: 8, bottom: 4 },
    },
  }

  return (
    <>
      <ChargerDetailsModal charger={detailsCharger} onClose={() => setDetailsCharger(null)} />
      {isShowingFilterModal && chargersWithLogs && (
        <ChargerFilterModal chargers={chargersWithLogs} filter={chargerFilter} onChange={setChargerFilter} onClose={() => setIsShowingFilterModal(false)} />
      )}
      <PageTitle label={t('logs_errorLogs')} />
      <ToolbarContainer>
        <div>
          <ButtonWithIcon icon="general.refresh" label={t('chargers_refresh')} onClick={handelRefresh} backgroundColor="success" iconColor="white" />
          <RefreshTimerLabel>{lastRefresh}</RefreshTimerLabel>
        </div>
      </ToolbarContainer>
      {chartData && (
        <ChartContainer style={{ cursor: 'pointer' }} onClick={() => setIsChartModalOpen(true)}>
          <Line data={chartData} options={smallChartOptions} />
        </ChartContainer>
      )}
      {isChartModalOpen && modalChartData && (
        <Modal onEscape={() => setIsChartModalOpen(false)}>
          <div style={{ width: 'min(max(60vw, 768px), calc(100vw - 72px))', height: '50dvh', padding: '24px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
              <Icon path="general.x" width="20px" height="20px" onClick={() => setIsChartModalOpen(false)} />
            </div>
            {/* @ts-expect-error Chart.js options type inference is overly strict */}
            <Line data={modalChartData} options={modalChartOptions} />
          </div>
        </Modal>
      )}
      {chargersWithLogs ? <>
        <FilterContainer>
          <InputWithIcon onInput={(input) => setSearchTerm(input)} width="max(12vw, 153.6px)" placeholder={t('chargers_search')} icon="general.search" iconColor="gray_600" />
          <WhiteSpace width="16px" />
          <ButtonWithIcon icon="general.filter" label={countActiveChargerFilters(chargerFilter) > 0 ? `${t('chargers_filter')} (${countActiveChargerFilters(chargerFilter)})` : t('chargers_filter')} onClick={() => setIsShowingFilterModal(true)} backgroundColor={countActiveChargerFilters(chargerFilter) > 0 ? 'primary' : 'dark'} iconColor="white" />
          <WhiteSpace width="16px" />
          <FilterButton $active={filter == 'all'} $themeColor="gray_600" onClick={() => setFilter('all')}>
            {t('logs_all')}
          </FilterButton>
          <FilterButton $active={filter == 'error'} $themeColor="danger" onClick={() => setFilter('error')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon path="general.error" width="14px" height="14px" color={filter == 'error' ? 'white' : 'gray_700'} />
              {t('logs_errors')}
            </div>
          </FilterButton>
          <FilterButton $active={filter == 'warning'} $themeColor="warning" onClick={() => setFilter('warning')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon path="general.warning" width="14px" height="14px" color={filter == 'warning' ? 'white' : 'gray_700'} />
              {t('logs_warnings')}
            </div>
          </FilterButton>
          <WhiteSpace width="16px" />
          <FilterButton $active={codeFilter == 'evse'} $themeColor="primary" onClick={() => setCodeFilter(codeFilter == 'evse' ? 'all' : 'evse')}>
            EVSE
          </FilterButton>
          <WhiteSpace width="16px" />
          <div style={{ display: 'flex', alignItems: 'center', backgroundColor: '#EFF2F5', borderRadius: '40px', paddingRight: '10px' }}>
            <Dropdown handleChoose={(value: string) => {
              const map: { [key: string]: TimeFilter } = {
                [t('logs_last1hour')]: '1h',
                [t('logs_last24hours')]: 1,
                [t('logs_last48hours')]: 2,
                [t('logs_last7days')]: 7,
                [t('logs_last14days')]: 14,
                [t('logs_last28days')]: 28,
                [t('logs_all')]: 'all',
                [t('logs_custom')]: 'custom',
              }
              setTimeFilter(map[value] || 2)
              setTimeFilterLabel(value)
            }} title={{ label: timeFilterLabel || t('logs_last48hours') }} options={[
              { label: t('logs_last1hour') },
              { label: t('logs_last24hours') },
              { label: t('logs_last48hours') },
              { label: t('logs_last7days') },
              { label: t('logs_last14days') },
              { label: t('logs_last28days') },
              { label: t('logs_all') },
              { label: t('logs_custom') },
            ]} width="130px" />
            <Icon path="general.downArrow" width="10px" height="10px" color="gray_600" marginleft="-4px" />
          </div>
          {timeFilter == 'custom' && (
            <div style={{ marginTop: '-12px', marginLeft: '12px' }}>
              <DateRangePickerWithPresets showPresets={false} onSelect={(range) => setCustomDateRange(range)} defaultDate={customDateRange} />
            </div>
          )}
          <WhiteSpace width="16px" />
          <FilterButton $active={allOpen} $themeColor="gray_600" onClick={toggleAllChargers}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Icon path={allOpen ? "general.upArrow" : "general.downArrow"} width="12px" height="12px" color={allOpen ? 'white' : 'gray_700'} />
              {allOpen ? t('logs_collapseAll') : t('logs_expandAll')}
            </div>
          </FilterButton>
        </FilterContainer>
        <ListContainer>
          {chargersWithLogs.filter(chargerHasMatchingLogs).filter(charger => chargerMatchesFilter(charger, chargerFilter)).map(charger => {
            const filteredLogs = chargerMatchesSearch(charger)
              ? applyCodeFilter(applyTimeFilter(charger.logs).filter(log => filter == 'all' || getLogCodeType(log.logCode) == filter))
              : getFilteredLogs(charger.logs)
            const timeFilteredLogs = applyTimeFilter(charger.logs)
            const counts = getLogCounts(timeFilteredLogs)
            if (filteredLogs.length == 0) return null

            return (
              <ChargerRow key={charger.chargerId} $isOpen={openChargers[charger.chargerId]}>
                <ChargerRowInner onClick={searchTerm.length >= 3 ? undefined : () => toggleCharger(charger.chargerId)} style={searchTerm.length >= 3 ? { cursor: 'default' } : {}}>
                  <Icon path="general.charger" width="30px" height="30px" marginright="16px" />
                  <div style={{ width: '250px', minWidth: '250px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <ChargerId>{charger.chargerId}</ChargerId>
                      <a href={`/chargers/${charger.chargerId}`} onClick={(e) => e.stopPropagation()}>
                        <Icon path="general.externalLink" width="12px" height="12px" color="gray_400" />
                      </a>
                    </div>
                    <ChargerEmail>{charger.userEmail}</ChargerEmail>
                    {charger.localName && charger.localName != ' ' && <ChargerEmail>{charger.localName}</ChargerEmail>}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginLeft: '24px', width: '300px', minWidth: '300px' }}>
                    <div style={{ width: '140px', minWidth: '140px' }}>
                      {(filter == 'all' || filter == 'error') && counts.errors > 0 && <LogCount $themeColor="danger" $bgColor="dangerLight"><Icon path="general.error" width="12px" height="12px" color="danger" />{t('logs_errors')}: {counts.errors}</LogCount>}
                    </div>
                    {(filter == 'all' || filter == 'warning') && counts.warnings > 0 && <LogCount $themeColor="warningActive" $bgColor="warningLight"><Icon path="general.warning" width="12px" height="12px" color="warningActive" />{t('logs_warnings')}: {counts.warnings}</LogCount>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ fontSize: '10px', color: '#A1A5B7', backgroundColor: '#F5F8FA', borderRadius: '6px', padding: '3px 8px' }}>FW <span style={{ fontWeight: 600 }}>{charger.fwVersion || '-'}</span></span>
                    <span style={{ fontSize: '10px', color: '#A1A5B7', backgroundColor: '#F5F8FA', borderRadius: '6px', padding: '3px 8px' }}>SW <span style={{ fontWeight: 600 }}>{charger.swVersion || '-'}</span></span>
                    <span style={{ fontSize: '10px', color: '#A1A5B7', backgroundColor: '#F5F8FA', borderRadius: '6px', padding: '3px 8px' }}>OS <span style={{ fontWeight: 600 }}>{charger.osVersion || '-'}</span></span>
                  </div>
                  <LastLogTime>{filteredLogs[0].day.replace(/-/g, '.')} {filteredLogs[0].time}</LastLogTime>
                  <span style={{ display: 'inline-flex', marginRight: '16px' }} onClick={(e) => e.stopPropagation()}>
                    <ChargerDetailsHint onClick={() => setDetailsCharger(charger)} title={t('chargers_details')} />
                  </span>
                  {searchTerm.length < 3 && <ArrowContainer $isOpen={openChargers[charger.chargerId]}>
                    <Icon path="general.forwardArrow" width="16px" height="16px" color="gray_600" />
                  </ArrowContainer>}
                </ChargerRowInner>
                {(openChargers[charger.chargerId] || (searchTerm.length >= 3)) && filteredLogs.length > 0 && (
                  <LogsDropdown>
                    <Table key={`${charger.chargerId}_${searchTerm}_${filter}_${timeFilter}_${codeFilter}_${customDateRange}`} id={'ErrorLogs'} height="auto" isSearchEnabled={false} isPaginationEnabled={false} columns={[
                      { width: '15px' },
                      { label: t('chargerLogs_lineNumber'), width: '7%' },
                      { label: t('chargerLogs_time'), width: '5%' },
                      { width: '6%' },
                      { label: t('chargerLogs_appTimestamp'), width: '8%' },
                      { label: t('chargerLogs_type'), width: '8%' },
                      { label: t('chargerLogs_message') }
                    ]} data={filteredLogs.map(log => {
                      const logLink = `/chargers/${log.chargerId}/logs?dateFilter=${log.day}&lineNumber=${log.lineNumber}`
                      return [
                      {
                        link: logLink,
                        custom: <LogIndicator $code={getLogCodeType(log.logCode)} />
                      }, {
                        link: logLink,
                        sortBy: parseInt(log.lineNumber),
                        value: {
                          type: TableCellTypes.Label,
                          value: log.lineNumber
                        }
                      }, {
                        link: logLink,
                        sortBy: `${new Date(`${log.day} ${log.time}`).valueOf()} ${parseInt(log.lineNumber)}`,
                        value: {
                          type: TableCellTypes.Label,
                          value: log.day.replace(/-/g, '.'),
                        }
                      }, {
                        link: logLink,
                        value: {
                          type: TableCellTypes.Text,
                          value: log.time
                        }
                      }, {
                        link: logLink,
                        sortBy: log.appTs,
                        value: {
                          type: TableCellTypes.Text,
                          value: (log.appTs || "-").toString()
                        }
                      }, {
                        link: logLink,
                        sortBy: log.logCode,
                        custom: <LogCode $code={getLogCodeType(log.logCode)}>{getLogCodeLabel(log.logCode)}</LogCode>
                      }, {
                        link: logLink,
                        sortBy: log.message,
                        value: {
                          type: TableCellTypes.Text,
                          value: log.message.replace(/,/g, ', ')
                        }
                      }
                    ]})} />
                  </LogsDropdown>
                )}
              </ChargerRow>
            )
          })}
        </ListContainer>
      </> : <>
        <div style={{ width: "100%", textAlign: "center", marginTop: "80px", fontSize: "18px", fontWeight: "700" }}>
          <LoaderDots />
        </div>
      </>}
    </>
  )
}
